import { logClosePickDialogOpen, logClosePickEventualsTotals } from 'nike-ui/src/common/logging/cashin/log';
import { fromJS, List, Map } from 'immutable';
import i18n from '../../common/i18n';
import { EvaluationState, ExtraType, ModificationState } from './constants';
import { formatNumber } from '../../common/util';
import { dispatch } from 'nike-re-framejs';
import { capitalize, throttle } from 'lodash';
import { nuiCtx } from '../../common/initialization';

export const cashinActivateClosingMode = ticketId => db => {
    const offer = db.getIn(['ticketus', 'cashin', 'tickets', ticketId]);
    logClosePickDialogOpen({ ticketId, offer: fromJS(offer) });
    hideCashinDialogIfPresent(db);
    return db.setIn(['ticketus', 'closing', 'ticketId'], ticketId);
};

export const clearCashinClosingMode = db => db.deleteIn(['ticketus', 'closing']);

export const hideCashinDialogIfPresent = db => {
    if (db.getIn(['ticketus', 'cashin', 'current-opened'])) {
        dispatch(['ticketus/cashin/close-auto-cashin-dialog']);
    }
};

export const saveIsLoading = (isLoading = null, ticketId) => db => {
    if (isLoading) {
        return db.setIn(['ticketus', 'closing', ticketId, 'isLoading'], isLoading);
    }
    return db.deleteIn(['ticketus', 'closing', ticketId, 'isLoading']);
};

export const saveEventualTotals = (totals, ticketId) => db =>
    db
        .setIn(['ticketus', 'closing', ticketId, 'eventualTotals'], fromJS(totals))
        .deleteIn(['ticketus', 'closing', ticketId, 'isLoading']);

export const startClosingTransaction = ({ commandResult }, ticketId) => db => {
    return setActiveTransactionId(
        { commandResult },
        ticketId
    )(db).setIn(['ticketus', 'closing', 'transaction-started'], commandResult?.status === 'SUCCESS');
};

export const setActiveTransactionId = ({ commandResult }, ticketId) => db =>
    db.setIn(['ticketus', 'activeTransactions', ticketId, 'transactionId'], commandResult?.transactionId);

export const deleteActiveTransactionId = ticketId => db => db.deleteIn(['ticketus', 'activeTransactions', ticketId]);

const isBeingClosed = (betId, pick) => {
    return (betId.betId || betId) === Number(pick.betId);
};

export const createCloseRequest = (db, checkBoxValue, requestedEventualWin) => {
    const ticketId = db.getIn(['ticketus', 'closing', 'ticketId']);
    const affectedPicks = db.getIn(['ticketus', 'closing', ticketId, 'chosenPicks']).toJS();
    const cashinClosingTransactionRequest = {
        customerChangeApproval: checkBoxValue,
        requestedEventualWin,
        affectedPicks
    };
    return { cashinClosingTransactionRequest, ticketId };
};

export const getEvalEventualTotalsNikeUi = throttle((ticket, cashinOffer, cashinClosingBetIds, cashinusClient) => {
    const ticketId = cashinOffer.ticketId;
    const picks = cashinOffer.picks;
    const cashinOfferVersion = cashinOffer.version;
    const evaluateEventualsTotalsPayload = {
        platform: capitalize(nuiCtx.platform),
        registrationChannel: ticket.get('platform'),
        oddsAdvantage: ticket
            .get('extras')
            ?.find(extra => extra.get('type') === ExtraType.oddsAdvantage)
            ?.get('multiplier'),
        automaticOddsAdvantageId: ticket
            .get('extras')
            ?.find(extra => extra.get('type') === ExtraType.automaticOddsAdvantage)
            ?.get('id'),
        bonusGameId: ticket.get('bonusGameExtra')?.get('id'),
        picks: picks.map(pick => {
            return {
                eventualOdds: pick.eventualOdds,
                currentCashinOdds: pick.currentCashinOdds,
                beingClosed: cashinClosingBetIds.some(betId => isBeingClosed(betId, pick)),
                evaluationState: pick.evaluationState,
                modificationState: pick.modificationState,
                betId: Number(pick.betId)
            };
        })
    };

    cashinusClient
        .post(`cashin/v4/${ticketId}/${cashinOfferVersion}/evaluate-eventual-totals`, evaluateEventualsTotalsPayload)
        .then(response => {
            dispatch(saveEventualTotals(response, ticketId));
            logClosePickEventualsTotals({ ticketId, offer: fromJS(cashinOffer), response });
        })
        .catch(e => {
            console.error(e);
            dispatch(saveEventualTotals({}, ticketId));
            logClosePickEventualsTotals({ ticketId, offer: fromJS(cashinOffer), error: e });
        })
        .finally(() => dispatch(saveIsLoading(false, ticketId)));
}, 1000);

export const countPicksAvailableToClose = picks =>
    picks.filter(
        pick =>
            pick.get('evaluationState') === EvaluationState.REGISTERED &&
            pick.get('modificationState') === ModificationState.UNMODIFIED
    );

export const calculateNewAutomaticOddsAdvantage = (eventualTotals, extras, cashinIsLoading) => {
    if (!extras || !extras.size) {
        return null;
    }

    const automaticOddsAdvantage = extras
        .filter(extra => extra.get('type') === ExtraType.automaticOddsAdvantage)
        .first();

    if (!automaticOddsAdvantage) {
        return null;
    }

    const currentExtraOdds =
        automaticOddsAdvantage.get('multiplierAfterEvaluation') || automaticOddsAdvantage.get('multiplier');

    if (cashinIsLoading === true) {
        return { currentExtraOdds: formatNumber(currentExtraOdds), newExtraOdds: '...' };
    }

    let newExtraOdds = '...';

    if (eventualTotals?.automaticOddsAdvantage === null) {
        newExtraOdds = i18n('nui.ticketus.detailTiketu.extraKurzLost');
    } else if (eventualTotals?.automaticOddsAdvantage) {
        newExtraOdds = formatNumber(eventualTotals?.automaticOddsAdvantage);
    }

    return { currentExtraOdds: formatNumber(currentExtraOdds), newExtraOdds };
};

export const sortBets = betList =>
    betList.sort((a, b) => {
        return (
            sortByGroup(a.group, b.group) ||
            sortByDate(a.expirationDate, b.expirationDate) ||
            sortByOpponent(a.opponents, b.opponents)
        );
    });

export const sortByGroup = (a, b) => {
    if (a && b) {
        if (a === 'T' && b !== 'T') return -1;
        if (a !== 'T' && b === 'T') return 1;
        return a.localeCompare(b);
    }
    return 0;
};

export const sortByDate = (a, b) => {
    if (a === b) return 0;
    return new Date(a) - new Date(b) > 0 ? 1 : -1;
};

export const sortByOpponent = (a, b) => {
    if (a && b) return a[0].localeCompare(b[0]);
    return 0;
};

export const toggleChosenPicks = ({ ticketId, betId, selectionCode }) => db => {
    const value = { betId, selectionCode };
    return db.updateIn(['ticketus', 'closing', ticketId], Map(), ticket => {
        return ticket.update('chosenPicks', List(), chosenPicks => toggleInArrayOfBets(chosenPicks, value));
    });
};

export const toggleDisabledPicks = ({ ticketId, betId, selectionCode }) => db => {
    const value = { betId, selectionCode };
    return db.updateIn(['ticketus', 'closing', ticketId], Map(), ticket => {
        return ticket.update('disabledChosenPicks', List(), chosenPicks => toggleInArrayOfBets(chosenPicks, value));
    });
};

export const toggleChosenAndDisabled = (input, db) => {
    const db2 = toggleChosenPicks(input)(db);
    return toggleDisabledPicks(input)(db2);
};

function getChosenPicks(db, ticketId, betId) {
    return db
        .getIn(['ticketus', 'closing', ticketId, 'chosenPicks'], List())
        .filter(bet => bet.get('betId') === betId)
        .first();
}

function getChosedDisbledPicks(db, ticketId, betId) {
    return db
        .getIn(['ticketus', 'closing', ticketId, 'disabledChosenPicks'], List())
        .filter(bet => bet.get('betId') === betId)
        .first();
}

export const disableUnavailablePickIfChosen = ({ ticketId, betId, selectionCode }) => db => {
    const chosenPick = getChosenPicks(db, ticketId, betId);
    const disabledPick = getChosedDisbledPicks(db, ticketId, betId);

    if (chosenPick && !disabledPick) {
        return toggleChosenAndDisabled({ ticketId, betId, selectionCode }, db);
    }

    return db;
};

export const enableRunningPickIfNotChosen = ({ ticketId, betId, selectionCode }) => db => {
    const chosenPick = getChosenPicks(db, ticketId, betId);
    const disabledPick = getChosedDisbledPicks(db, ticketId, betId);

    if (!chosenPick && disabledPick) {
        return toggleChosenAndDisabled({ ticketId, betId, selectionCode }, db);
    }

    return db;
};

function removePickFromList(betId) {
    return picks => picks.filter(bet => bet.get('betId') !== betId);
}

export const deleteFromChosenAndDisabledPicks = ({ ticketId, betId }) => db => {
    return db.updateIn(['ticketus', 'closing', ticketId], Map(), ticket => {
        return ticket
            .update('disabledChosenPicks', List(), removePickFromList(betId))
            .update('chosenPicks', List(), removePickFromList(betId));
    });
};

// FIXME: prepisat na immutable, len fakt uz na to nemam nervy :(
function toggleInArrayOfBets(immutableBets, bet) {
    const cashinClosingChosenDisabledPicks = immutableBets.toJS();
    const chosenDisabledPicksArr = cashinClosingChosenDisabledPicks.find(({ betId }) => betId === bet.betId)
        ? cashinClosingChosenDisabledPicks.filter(({ betId }) => betId !== bet.betId)
        : [...cashinClosingChosenDisabledPicks, bet];
    return fromJS(chosenDisabledPicksArr);
}

//
// Chcem zatvorit uzt mode len v pripade, ze som danu activeTransaction spustil v aktualnom okne, na aktualnom zariadeni,
// zamedzime automatickemu zatvoreniu uzatvaracieho modu na inom zariadeni, kde som si transakciu nestartol.
export const closeClosingModeOnCurrentDevice = ({ db, ticketId, cashin }) => {
    // ak mam closing mode otvoreny na inom zariadeni, ako som startol transakciu, tak toto bude undefined:
    const activeTransactionsId = db.getIn(['ticketus', 'activeTransactions', ticketId, 'transactionId']);

    // tento if kontroluje, ci som transakciu ktorej prisli data cez push, zacal na aktualnom zariadeni
    if (activeTransactionsId && activeTransactionsId === cashin.getIn(['activeTransaction', 'id'])) {
        return db.deleteIn(['ticketus', 'closing']);
    }
    return db;
};
